import { useInfiniteQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { getNextPageParamFromPaginatedResult } from '@/api/infiniteQueries';
import { graphql } from '@/gql';
import {
  InboundEmailInboxFilter,
  InboundEmailInboxInput,
  InboundEmailStatus,
  PaginationInput,
} from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import inboundEmailKeys from './keyFactory';

const FILTER_TO_STATUSES: Record<
  InboundEmailInboxFilter,
  InboundEmailStatus[]
> = {
  UNASSIGNED: ['UNREAD', 'READ'],
  ASSIGNED: ['ASSIGNED'],
  ARCHIVED: ['ARCHIVED'],
};

const GetInboundEmailInbox = graphql(`
  query InboundEmailInbox(
    $pagination: PaginationInput
    $input: InboundEmailInboxInput
  ) {
    me {
      inboundEmailInbox(pagination: $pagination, input: $input) {
        items {
          id
          addressType
          fromEmail
          fromName
          originalFrom
          toEmails
          subject
          status
          assignedProject {
            id
            reference
          }
          createdAt
        }
        limit
        offset
        totalCount
      }
    }
  }
`);

const useGetInboundEmailInbox = (
  pagination?: PaginationInput,
  input?: InboundEmailInboxInput
) => {
  const queryFn = ({ pageParam }: { pageParam: number }) => {
    return gqlClient.request(GetInboundEmailInbox, {
      pagination: {
        offset: pageParam,
        limit: pagination?.limit || PAGINATION_ARG_DEFAULTS.limit,
      },
      input,
    });
  };

  return useInfiniteQuery({
    queryKey: inboundEmailKeys.inbox(input),
    queryFn,
    initialPageParam: pagination?.offset || PAGINATION_ARG_DEFAULTS.offset,
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.me.inboundEmailInbox
    ),
    placeholderData: (previousData) => {
      if (!previousData || !input?.filter) return previousData;

      const allowedStatuses = FILTER_TO_STATUSES[input.filter];

      return {
        ...previousData,
        pages: previousData.pages.map((page) => {
          const filtered = page.me.inboundEmailInbox.items.filter((item) =>
            allowedStatuses.includes(item.status)
          );
          return {
            ...page,
            me: {
              ...page.me,
              inboundEmailInbox: {
                ...page.me.inboundEmailInbox,
                items: filtered,
                totalCount: filtered.length,
              },
            },
          };
        }),
        pageParams: previousData.pageParams,
      };
    },
  });
};

export default useGetInboundEmailInbox;
